import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { SaveDataHandler } from "src/modules/app-template/models/save-data-handler.model";
import { DatabaseUser } from 'src/modules/database/shared/database-user.entity';
import { AdminBaseEditComponent } from 'src/modules/sm-base/components/admin-base-edit/admin-base-edit.component';
import { ButtonDefinition } from 'src/modules/sm-base/models/button-definition.model';
import { FrontendFieldValueChangedEvent } from 'src/modules/sm-base/models/frontend-field-value-changed-event.model';
import { RestEndpoint } from "src/modules/sm-base/models/rest-endpoint.model";
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { FrontendFieldError } from 'src/modules/sm-base/netgen/FrontendFieldError';
import { Dates } from 'src/modules/utils/shared/dates';
import { Objects } from 'src/modules/utils/shared/objects';
import { ProjectPlanningTools } from '../../models/project-planning-tools.model';
import { CalendarEvent } from '../../netgen/CalendarEvent';
import { CalendarEventKind } from '../../netgen/CalendarEventKind';
import { CalendarEventParticipant } from '../../netgen/CalendarEventParticipant';
import { CalendarEventResource } from '../../netgen/CalendarEventResource';
import { CalendarEventSource } from '../../netgen/CalendarEventSource';
import { CalendarEventType } from '../../netgen/CalendarEventType';
import { CrudWithPreviewDto } from "../../netgen/CrudWithPreviewDto";
import { Resource } from '../../netgen/Resource';
import { ProjectPlanningService } from '../../services/project-planning.service';
import { Arrays } from 'src/modules/utils/shared/arrays';

@Component({
    selector: 'app-edit-calendar-event',
    templateUrl: '../../../sm-base/components/admin-base-edit/admin-base-edit.component.html',
    styleUrl: '../../../sm-base/components/admin-base-edit/admin-base-edit.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditCalendarEventAppointmentComponent extends AdminBaseEditComponent<CalendarEvent> {

    service = inject(ProjectPlanningService);

    definition = ProjectPlanningTools.definitionCalendarEvent;

    users: DatabaseUser[];
    resources: Resource[];
    eventTypes: CalendarEventType[];
    isCopy = false;
    copy: CalendarEvent;

    getForm(): FrontendForm {
        let outlook = this.item.source == CalendarEventSource.outlook;
        return this.app.tForm("entity.CalendarEvent.fields", new FrontendForm([
            outlook ? new FrontendField("readOnlyInfo", this.app.t("entity.CalendarEvent.other.readOnlyInfo"), FrontendFieldType.string, {
                hasControl: false,
                labelStyleClass: "font-size-125rem text-red-500"
            }) : null,
            new FrontendField("organisatorId", this.app.t("entity.Calendar.entityName"), FrontendFieldType.dropdown, {
                mandatory: true, 
                listItems: FrontendFieldListItem.make(this.users, item => [item.id, item.getFullName()]),
                dropdownCanFilter: true,
                emptyValues: [null, 0],
                enabled: this.isInsert()
            }),
            new FrontendField("title", "", FrontendFieldType.string, { 
                mandatory: true, 
                maxLength: 100,
                guiGroup: "title", 
                guiWidthInGroup: 5,
                enabled: !outlook 
            }),
            new FrontendField("typeId", "", FrontendFieldType.dropdown, { 
                mandatory: true, 
                guiGroup: "title", 
                guiWidthInGroup: 2,
                listItems: FrontendFieldListItem.make(this.eventTypes, item => [item.id, item.title], true),
                dropdownCanFilter: true,
                //TODO undefined muss hier aufgezählt werden, weil projectId = undefined ist bei Neuanlage und das sonst nicht als "nicht befüllt" erkannt wird
                emptyValues: [null, undefined, 0],
                enabled: !outlook 
            }),
            new FrontendField("note", "", FrontendFieldType.textArea, { 
                fixedHeight: "100px",
                enabled: !outlook
            }),
            new FrontendField("createdBy", "", FrontendFieldType.string, { 
                enabled: false,
                guiGroup: "createdBy", 
                guiWidthInGroup: 1 
            }),
            new FrontendField("_1", "", FrontendFieldType.splitter),
            new FrontendField("allDay", "", FrontendFieldType.bool, {
                onValueChanged: this.allDayChanged.bind(this),
                callValueChangedAfterLoad: true,
                enabled: !outlook
            }),
            new FrontendField("startDate", "", FrontendFieldType.date, { 
                mandatory: true,
                guiGroup: "startDate", 
                guiWidthInGroup: 1,
                enabled: !outlook,
                dateIntervalToField: "endDate",
                dateIntervalEmptyAllowed: false,
                onValueChanged: this.startDateChanged.bind(this)
            }),
            new FrontendField("endDate", "", FrontendFieldType.date, { 
                mandatory: true,
                guiGroup: "startDate", 
                guiWidthInGroup: 1,
                enabled: !outlook,
                dateIntervalFromField: "startDate",
                dateIntervalEmptyAllowed: false,
                toForm: (value, _) => value ? this.form.getValueBool("allDay") ? Dates.add(value as Date, "day", -1) : (value as Date)
                            : null,
                fromForm: (value, _) => value ? this.form.getValueBool("allDay") ? Dates.add(value as Date, "day", 1) : (value as Date)
                            : null
            }),
            new FrontendField("_4", "", FrontendFieldType.splitter),
            new FrontendField("participantsInternal", this.app.t("entity.CalendarEvent.other.participantsInternal"), FrontendFieldType.dropdownMulti, { 
                listItems: FrontendFieldListItem.make(this.users, item => [item.id, item.getFullName()], true),
                dropdownCanFilter: true,
                enabled: !outlook,
                fromForm: (value, _) => (value as number[]).map(id => this.item.participants.find(res => res.userId == id) ?? Objects.fromPlain(CalendarEventParticipant, {
                    userId: id
                })),
                toForm: (value, _) => (value as CalendarEventParticipant[]).map(r => r.userId)            
            }),
            new FrontendField("participantsExternal", this.app.t("entity.CalendarEvent.other.participantsExternal"), FrontendFieldType.dropdownMulti, { 
                dropdownCanFilter: true,
                dropdownAutoComplete: true,
                dropdownEditable: true,
                dropdownButtonVisible: false,
                enabled: !outlook    
            }),
            new FrontendField("resources", "", FrontendFieldType.dropdownMulti, { 
                listItems: FrontendFieldListItem.make(this.resources, item => [item.id, item.title], true),
                dropdownCanFilter: true,
                enabled: !outlook,
                fromForm: (value, _) => (value as number[]).map(id => this.item.resources.find(res => res.resourceId == id) ?? Objects.fromPlain(CalendarEventResource, {
                    resourceId: id
                })),
                toForm: (value, _) => (value as CalendarEventResource[]).map(r => r.resourceId)            
            }),
            ...ProjectPlanningTools.createReminderFields(this.app, () => this.form)
]));
    }

    async initAdditionalData(): Promise<void> {
        this.users = await this.service.getActiveUsers();
        this.resources = await this.service.getResources();  
        this.eventTypes = await this.service.getEventTypes();
        let currentUser = this.service.getCurrentUser();
        let copiedOrganisatorId: number = null;
        if (this.isInsert()) {
            this.item.createdBy = currentUser?.getFullName();
            if (this.isCopy) {
                if (this.item.organisatorId == null) {
                    //Wenn es sich um einen Termin aus Outlook handelt, ist organisatorId normalerweise nicht gesetzt
                    this.item.organisatorId = Arrays.getSafe(this.item.participants, 0)?.userId;
                }
                copiedOrganisatorId = this.item.organisatorId;
            }
            else {
                this.item.organisatorId ??= currentUser?.id;
            }
        }
        if (this.isCopy && copiedOrganisatorId != null) {
            this.item.participants = this.item.participants.filter(p => p.userId != copiedOrganisatorId);
        }
        (this.item as any).participantsInternal = this.item.participants.filter(p => p.userId != null);
        (this.item as any).participantsExternal = this.item.participants.filter(p => p.userId == null).map(p => p.mail);   
        this.copy = Objects.cloneDeep(this.item); 
        if (this.isCopy) {
            this.item.organisatorId = null;
        }    
    }

    getDialogButtons(): ButtonDefinition[] {
        let result = super.getDialogButtons();
        if (!this.isInsert() && !this.isCopy && this.item.isDeleteAllowed(this.service.getCurrentUser())) {
            result.push(new ButtonDefinition("delete", this.app.t("general.delete"), true, "fas fa-trash", "", "danger"));
        }
        if (this.item.source == CalendarEventSource.outlook) {
            result = result.filter(b => b.id != "save");
        }    
        return result;
    }

     async buttonClickedFromExtern(id: string): Promise<void> {
        if (id == "delete") {
            await this.service.deleteEvent(this.item, () => this.close(), this);
        }
        else {
            await super.buttonClickedFromExtern(id);
        }
    }

    validateForm(): boolean {
        return this.app.validateForm(this.form, this.isCopy ? () => {
            let item = this.form.get(Objects.cloneDeep(this.item), true);
                      item.participants = this.getParticipantsFromForm();
                      return this.copyRelevantFieldsEqual(item) ? [new FrontendFieldError("title", this.app.t("entity.CalendarEvent.other.copyValidation"))] : [];
        } : null);
    }

    async fromForm(): Promise<void> {
        await super.fromForm();
        this.item.kind = CalendarEventKind.appointment;
        this.item.participants = this.getParticipantsFromForm();
    }

    async upsert(item: CalendarEvent): Promise<boolean> {
        return (await this.app.saveDataHandler2(new SaveDataHandler<boolean>({
            componentView: this,
            worker: async () => {
                let result = await RestEndpoint.main().upsert(item).query({previewIfNeeded: true}).run(this.definition.apiEndpoint).get(CrudWithPreviewDto);
                if (!result.executed) {
                    if (!await this.app.dialog.yesNo(this.app.t("entity.CalendarEvent.other.previewWarning", {previewItems: Arrays.itemsToString(result.previewItems, "<br>")}), this.app.t("general.warning"))) {
                        return false;
                    }
                    await RestEndpoint.main().upsert(item).query({previewIfNeeded: false}).run(this.definition.apiEndpoint).get(CrudWithPreviewDto);
                }

                await this.app.trackChanges();
                return true;
            },
            navigateBack: !this.multiInsert,
            isSuccess: result => result
        }))).data;
    }

    private getParticipantsFromForm(): CalendarEventParticipant[] {
        return [
            ...(this.form.getValue("participantsInternal") as CalendarEventParticipant[]) ?? [],
            ...((this.form.getValue("participantsExternal") as string[]) ?? [])
                .map(mail => Objects.fromPlain(CalendarEventParticipant, {
                mail
            }))];
    }

    private copyRelevantFieldsEqual(item: CalendarEvent): boolean {
        return item.kind == this.copy.kind &&
            item.title == this.copy.title &&
            item.typeId == this.copy.typeId &&
            item.note == this.copy.note &&
            item.organisatorId == this.copy.organisatorId &&
            item.allDay == this.copy.allDay &&
            Dates.eq(item.startDate, this.copy.startDate) &&
            Dates.eq(item.endDate, this.copy.endDate) &&
            item.reminderType == this.copy.reminderType &&
            item.reminderDeltaMinutes == this.copy.reminderDeltaMinutes &&
            item.participantsEqual(this.copy) &&
            Arrays.equalsFlatUnordered((item.resources ?? []).map(resource => resource.resourceId), (this.copy.resources ?? []).map(resource => resource.resourceId));
    }

    allDayChanged(): void {
        let allDay = this.form.getValueBool("allDay");
        this.form.getField("startDate").type = allDay ? FrontendFieldType.date : FrontendFieldType.dateTime;
        this.form.getField("endDate").type = allDay ? FrontendFieldType.date : FrontendFieldType.dateTime;
        if (!allDay) {
            this.form.setValue("startDate", this.form.getValueDate("startDate") != null ? Dates.withoutTime(this.form.getValueDate("startDate")) : null);
            this.form.setValue("endDate", this.form.getValueDate("endDate") != null ? Dates.withoutTime(this.form.getValueDate("endDate")) : null);
            this.lastValidStartDate = this.form.getValueDate("startDate");
        }
    }

    lastValidStartDate: Date = null;
    
    startDateChanged(evt: FrontendFieldValueChangedEvent): void {
        let startDate = this.form.getValueDate("startDate");
        let endDate = this.form.getValueDate("endDate");
        if (startDate != null && endDate != null && this.lastValidStartDate != null && evt.byUser) {
            let diff = Dates.diffMillis(this.lastValidStartDate, startDate);
            this.form.setValue("endDate", Dates.add(endDate, "milli", diff));
        }
        if (Dates.is(startDate)) {
            this.lastValidStartDate = startDate;
        }
    }

}
